import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Building2, Users, Settings, BarChart3, Shield, Network, Plus, Search, Filter, Crown, TrendingUp, Clock } from 'lucide-react';
import { PEOProviderForm } from '../peo/PEOProviderForm';
import { OrganizationalRolesManager } from './OrganizationalRolesManager';
import { TeamStructureManager } from './TeamStructureManager';
import { OrgChartBuilder } from './OrgChartBuilder';
import { PermissionMatrix } from './PermissionMatrix';
import { AnalyticsTab } from './AnalyticsTab';
import { PendingVerificationsCard } from './PendingVerificationsCard';
import { RecentActivityCard } from "./RecentActivityCard";
import { RoleDistributionCard } from "./RoleDistributionCard";
import { useOrganizationStats } from '@/hooks/peo-organizations/useOrganizationStats';
import { useOrganizationAnalytics } from '@/hooks/peo-organizations/useOrganizationAnalytics';
import type { PEOProvider } from '@/types/peo.types';

interface OrganizationManagementTabsProps {
  providerId: string;
  provider?: PEOProvider;
  onProviderUpdate?: () => void;
}

export const OrganizationManagementTabs: React.FC<OrganizationManagementTabsProps> = ({ 
  providerId,
  provider,
  onProviderUpdate 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState<string>('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditingProvider, setIsEditingProvider] = useState(false);

  // Fetch real organization data
  const { stats, isLoading: statsLoading } = useOrganizationStats(providerId);
  const { analytics, isLoading: analyticsLoading } = useOrganizationAnalytics(providerId);

  const handleProviderSave = () => {
    setIsEditingProvider(false);
    onProviderUpdate?.();
  };

  const handleProviderClose = () => {
    setIsEditingProvider(false);
  };

  return (
    <div className="space-y-6">
      {/* Search and Filter Controls */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search members..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={filterDepartment} onValueChange={setFilterDepartment}>
          <SelectTrigger className="w-48">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by department" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            <SelectItem value="sales">Sales</SelectItem>
            <SelectItem value="underwriting">Underwriting</SelectItem>
            <SelectItem value="operations">Operations</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="org-chart" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Org Chart
          </TabsTrigger>
          <TabsTrigger value="roles" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Roles
          </TabsTrigger>
          <TabsTrigger value="teams" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Teams
          </TabsTrigger>
          <TabsTrigger value="permissions" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Permissions
          </TabsTrigger>
          <TabsTrigger value="provider-details" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Provider
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="provider-details" className="space-y-6">
          <Card>
            <CardContent className="p-6">
              {isEditingProvider ? (
                <PEOProviderForm
                  provider={provider}
                  onSuccess={handleProviderSave}
                  onClose={handleProviderClose}
                />
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">Provider Information</h3>
                      <p className="text-sm text-muted-foreground">
                        View and manage provider details, certifications, and enterprise features
                      </p>
                    </div>
                    <Button
                      onClick={() => setIsEditingProvider(true)}
                      variant="default"
                    >
                      Edit Provider
                    </Button>
                  </div>

                  {provider && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <h4 className="font-medium text-sm text-muted-foreground mb-2">Basic Information</h4>
                        <div className="space-y-2">
                          <div>
                            <span className="text-sm font-medium">Name:</span>
                            <span className="ml-2 text-sm">{provider.name}</span>
                          </div>
                          <div>
                            <span className="text-sm font-medium">Employee Range:</span>
                            <span className="ml-2 text-sm">{provider.employee_range}</span>
                          </div>
                          <div>
                            <span className="text-sm font-medium">Years in Business:</span>
                            <span className="ml-2 text-sm">{provider.years_in_business}</span>
                          </div>
                          <div>
                            <span className="text-sm font-medium">Rating:</span>
                            <span className="ml-2 text-sm">{provider.rating}/5</span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium text-sm text-muted-foreground mb-2">Service Details</h4>
                        <div className="space-y-2">
                          <div>
                            <span className="text-sm font-medium">States Served:</span>
                            <div className="ml-2 text-sm">
                              {provider.states_served?.join(', ') || 'Not specified'}
                            </div>
                          </div>
                          <div>
                            <span className="text-sm font-medium">Specialties:</span>
                            <div className="ml-2 text-sm">
                              {provider.specialties?.join(', ') || 'Not specified'}
                            </div>
                          </div>
                          <div>
                            <span className="text-sm font-medium">ESAC Accredited:</span>
                            <span className="ml-2 text-sm">
                              {provider.is_esac_accredited ? 'Yes' : 'No'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium text-sm text-muted-foreground mb-2">Enterprise Status</h4>
                        <div className="space-y-2">
                          <div>
                            <span className="text-sm font-medium">Enterprise Features:</span>
                            <div className="ml-2 flex items-center space-x-2">
                              <span className={`text-sm ${(provider as any)?.enterprise_features_enabled ? 'text-green-600' : 'text-muted-foreground'}`}>
                                {(provider as any)?.enterprise_features_enabled ? 'Enabled' : 'Disabled'}
                              </span>
                              {(provider as any)?.enterprise_features_enabled && (
                                <Badge variant="outline" className="text-xs">
                                  <Crown className="h-3 w-3 mr-1" />
                                  Enterprise
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div>
                            <span className="text-sm font-medium">Subscription Tier:</span>
                            <span className="ml-2 text-sm capitalize">
                              {(provider as any)?.subscription_tier || 'basic'}
                            </span>
                          </div>
                          <div>
                            <span className="text-sm font-medium">Subscription Status:</span>
                            <span className={`ml-2 text-sm capitalize ${
                              (provider as any)?.subscription_status === 'active' ? 'text-green-600' : 
                              (provider as any)?.subscription_status === 'trialing' ? 'text-amber-600' :
                              (provider as any)?.subscription_status === 'past_due' ? 'text-red-600' :
                              'text-muted-foreground'
                            }`}>
                              {(provider as any)?.subscription_status || 'active'}
                            </span>
                          </div>
                          {(provider as any)?.referral_code && (
                            <div>
                              <span className="text-sm font-medium">Referral Code:</span>
                              <span className="ml-2 text-sm font-mono bg-muted px-1 rounded">
                                {(provider as any).referral_code}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Members</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {statsLoading ? '...' : stats.totalMembers}
                </div>
                <p className="text-xs text-muted-foreground">
                  Verified representatives
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Roles</CardTitle>
                <Settings className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {statsLoading ? '...' : stats.activeRoles}
                </div>
                <p className="text-xs text-muted-foreground">
                  Organizational roles defined
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Departments</CardTitle>
                <Building2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {statsLoading ? '...' : stats.departments}
                </div>
                <p className="text-xs text-muted-foreground">
                  Active departments
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending Applications</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {statsLoading ? '...' : stats.pendingApplications}
                </div>
                <p className="text-xs text-muted-foreground">
                  Awaiting review
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Pending Verifications Section */}
          <PendingVerificationsCard providerId={providerId} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RecentActivityCard providerId={providerId} />
            <RoleDistributionCard providerId={providerId} />
          </div>
        </TabsContent>

        <TabsContent value="org-chart">
          <OrgChartBuilder 
            providerId={providerId} 
            searchTerm={searchTerm}
            filterDepartment={filterDepartment}
          />
        </TabsContent>

        <TabsContent value="roles">
          <OrganizationalRolesManager 
            providerId={providerId}
            searchTerm={searchTerm}
          />
        </TabsContent>

        <TabsContent value="teams">
          <TeamStructureManager 
            providerId={providerId}
            searchTerm={searchTerm}
            filterDepartment={filterDepartment}
          />
        </TabsContent>

        <TabsContent value="permissions">
          <PermissionMatrix 
            providerId={providerId}
          />
        </TabsContent>

        <TabsContent value="analytics">
          <AnalyticsTab providerId={providerId} />
        </TabsContent>
      </Tabs>
    </div>
  );
};